import React, { useEffect, useState } from 'react';
import { useDocumentTitle } from '../../../../../../_hooks/useDocumentTitle';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { toast } from '../../../../../../CommonComponents/OPL/Toast/Toast';
import { useIsPdf } from '../../../../../hooks/useIsPdf';
import type { EssentialTeamRoleDTO } from '../../../../../types/goalsDTO';
import { Explanations } from '../../../../components/Explanations/Explanations';
import { useDeleteEssentialRoleMutation } from '../../api/useDeleteEssentialRoleMutation';
import { EstatePlanningTab } from '../../EstatePlanning.types';
import { AddEditEssentialRoleModal } from './AddEditEssentialRoleModal';
import { EssentialRolesTable } from './components/EssentialRolesTable/EssentialRolesTable';
import { useEssentialRoles } from './useEssentialRoles';

const NEW_ROLE_TEMPLATE: EssentialTeamRoleDTO = {
  Id: '',
  PredefinedKey: null,
  RoleName: '',
  Name: '',
  Email: null,
  PhoneNumber: null,
  Description: null,
};

export const EssentialRoles = () => {
  useDocumentTitle('Goals - Estate Planning Essential Roles');

  const [selectedRole, setSelectedRole] = useState<EssentialTeamRoleDTO | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<EssentialTeamRoleDTO | null>(null);

  const {
    mutateAsync: deleteRole,
    isLoading: isDeleting,
    error: deleteError,
    reset: resetDelete,
  } = useDeleteEssentialRoleMutation();

  useEffect(() => {
    resetDelete();
  }, [roleToDelete, resetDelete]);

  const isPrint = useIsPdf();

  const data = useEssentialRoles({ isPrint });

  if (!data) {
    return null;
  }

  const { roles, predefinedOptions } = data;

  return (
    <Box mb={isPrint ? '0' : '32'}>
      {!isPrint && (
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Explanations selectedTab={EstatePlanningTab.EssentialRoles} my="24" />

          <Button leftIcon="fas fa-plus" mt="24" onClick={() => setSelectedRole(NEW_ROLE_TEMPLATE)}>
            Add Essential Roles
          </Button>
        </Box>
      )}

      <EssentialRolesTable
        roles={roles}
        isPrint={isPrint}
        onEditRole={setSelectedRole}
        onDeleteRole={setRoleToDelete}
      />

      {selectedRole !== null && (
        <AddEditEssentialRoleModal
          isOpen
          teamRole={selectedRole}
          predefinedOptions={predefinedOptions}
          onRequestClose={() => setSelectedRole(null)}
        />
      )}

      <Modal
        isOpen={roleToDelete !== null}
        onRequestClose={() => setRoleToDelete(null)}
        aria={{ labelledby: 'delete-essential-role-header' }}
      >
        <ModalHeader as="h2" id="delete-essential-role-header">
          Remove Essential Roles?
        </ModalHeader>
        <ModalCloseButton onClick={() => setRoleToDelete(null)} />

        <ModalBody>
          {deleteError && <Alert variant="error" message={deleteError.message} mb="16" />}
          {roleToDelete && (
            <Text>
              Are you sure want to remove {roleToDelete.Name}?
            </Text>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="subtleInactive" type="button" onClick={() => setRoleToDelete(null)}>
            Cancel
          </Button>
          <Button
            type="button"
            isLoading={isDeleting}
            ml="8"
            onClick={async () => {
              if (!roleToDelete) {
                return;
              }

              try {
                await deleteRole({ essentialTeamRoleId: roleToDelete.Id });
                setRoleToDelete(null);
                toast.success('Essential role successfully removed.');
              } catch {
                // deleteError is shown in the modal
              }
            }}
          >
            Remove This Essential Role
          </Button>
        </ModalFooter>
      </Modal>
    </Box>
  );
};
